import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Award, Trophy, Search, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BadgesTab({ badgeOverview, setBadgeOverview, allUsers }) {
  const [badgeUserSearch, setBadgeUserSearch] = useState('');
  const [selectedUserBadges, setSelectedUserBadges] = useState(null);

  const searchUser = async () => {
    const found = allUsers.find(u => u.email?.toLowerCase().includes(badgeUserSearch.toLowerCase()));
    if (!found) { toast.error('User not found'); return; }
    try {
      const res = await axios.get(`${API}/admin/users/${found.user_id}/badges`);
      setSelectedUserBadges(res.data);
    } catch { toast.error('Failed to load user badges'); }
  };

  const refreshBadges = async (userId) => {
    const res = await axios.get(`${API}/admin/users/${userId}/badges`);
    setSelectedUserBadges(res.data);
    const bRes = await axios.get(`${API}/admin/badges/overview`);
    setBadgeOverview(bRes.data);
  };

  return (
    <div className="space-y-6">
      {badgeOverview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" />Badge Overview</CardTitle>
            <CardDescription>{badgeOverview.total_badges_earned} badges earned across {badgeOverview.total_users} users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {badgeOverview.badges?.map((b) => (
                <div key={b.badge_id} className="p-3 rounded-lg border bg-card text-center space-y-1" data-testid={`admin-badge-${b.badge_id}`}>
                  <span className="text-3xl block">{b.icon}</span>
                  <p className="text-xs font-semibold truncate">{b.name_en}</p>
                  <p className="text-[10px] text-muted-foreground">{b.requirement_type}: {b.requirement_value}</p>
                  <Badge variant={b.earned_count > 0 ? "default" : "secondary"} className="text-[10px]">{b.earned_count} earned</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" />Manage User Badges</CardTitle>
          <CardDescription>Search for a user to view, award, or revoke badges</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search user by email..." value={badgeUserSearch} onChange={(e) => setBadgeUserSearch(e.target.value)} className="pl-9" data-testid="badge-user-search" />
            </div>
            <Button size="sm" onClick={searchUser} data-testid="badge-search-btn"><Search className="h-4 w-4" /></Button>
          </div>

          {!selectedUserBadges && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {allUsers.filter(u => !badgeUserSearch || u.email?.toLowerCase().includes(badgeUserSearch.toLowerCase())).slice(0, 8).map(u => (
                <button key={u.user_id} className="w-full text-left p-2 rounded hover:bg-muted text-sm flex items-center justify-between"
                  onClick={async () => {
                    try { const res = await axios.get(`${API}/admin/users/${u.user_id}/badges`); setSelectedUserBadges(res.data); setBadgeUserSearch(u.email); } catch { toast.error('Failed to load badges'); }
                  }}>
                  <span>{u.email}</span><span className="text-xs text-muted-foreground">{u.name}</span>
                </button>
              ))}
            </div>
          )}

          {selectedUserBadges && (
            <div className="space-y-3" data-testid="user-badge-list">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{selectedUserBadges.user?.email}</p>
                  <p className="text-xs text-muted-foreground">{selectedUserBadges.earned_count}/{selectedUserBadges.total_count} badges earned{selectedUserBadges.user?.total_points ? ` | ${selectedUserBadges.user.total_points} XP` : ''}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedUserBadges(null)}><XCircle className="h-4 w-4" /></Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedUserBadges.badges?.map((b) => {
                  const userId = allUsers.find(u => u.email === selectedUserBadges.user?.email)?.user_id;
                  return (
                    <div key={b.badge_id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${b.earned ? 'bg-amber-500/5 border-amber-500/30' : 'opacity-60'}`} data-testid={`user-badge-${b.badge_id}`}>
                      <span className="text-2xl">{b.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{b.name_en}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{b.description_en}</p>
                        {b.earned && b.earned_at && <p className="text-[10px] text-amber-600">{new Date(b.earned_at).toLocaleDateString()}</p>}
                      </div>
                      {b.earned ? (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-500/10 h-7 px-2"
                          onClick={async () => { if (!userId) return; try { await axios.delete(`${API}/admin/users/${userId}/badges/${b.badge_id}`); toast.success(`Revoked: ${b.name_en}`); await refreshBadges(userId); } catch { toast.error('Failed to revoke'); }}}
                          data-testid={`revoke-${b.badge_id}`}>Revoke</Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10 h-7 px-2"
                          onClick={async () => { if (!userId) return; try { await axios.post(`${API}/admin/users/${userId}/badges/${b.badge_id}/award`); toast.success(`Awarded: ${b.name_en}`); await refreshBadges(userId); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to award'); }}}
                          data-testid={`award-${b.badge_id}`}>Award</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
